import { translationKey } from '../i18n';
import { localize } from '../i18n';
import useNativeImageRequests from '../hooks/useNativeImageRequests';
import { supportsCorrelatedImagePicker } from '../utils/nativeImageRequests';
import useNavigate from '../hooks/useSafeNavigate';
import useBackHandler from '../hooks/useBackHandler';
import LoadingLabel from '../components/LoadingLabel';
import PageLoading from '../components/PageLoading';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import WizardLayout from '../components/WizardLayout';
import ScrollDownIndicator from '../components/ScrollDownIndicator';
import AppToast from '../components/AppToast';
import StepConfirm from './medication-form/StepConfirm';
import {
    getDeviceUuid,
    getMedication,
    getMedications,
    updateMedication,
} from '../api';
import { syncWidget } from '../utils/widgetSync';
import WebviewMessenger from '../utils/WebviewMessenger';
import { compareVersions } from '../utils/version';
import { clearMedicationCaches } from '../utils/localCache';
import useMedicationPhotoEditing from '../hooks/useMedicationPhotoEditing';

const DAY_ORDER = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
const APP_RUNTIME_INFO_KEY = 'pill_reminder_app_runtime_info';
const MIN_NATIVE_PHOTO_VERSION = '1.0.5';

const initialForm = {
    name: '',
    memo: '',
    category: 'OTHER',
    intakeForm: 'ORAL',
    itemSeq: null,
    medicineSource: null,
    imageUrl: '',
    frequencyType: 'DAILY',
    specificDays: [],
    hourlyInterval: '',
    intervalDays: '',
    cycleDaysOn: '',
    cycleDaysOff: '',
    monthlyDays: [],
    dosageMethod: 'ANYTIME',
    startDate: '',
    endDate: '',
    indefinite: true,
    times: ['08:00'],
    dosagePerTime: 1,
    alertType: 'PUSH',
    alarmVolume: 80,
    reminderEnabled: true,
    reminderIntervalMinutes: 30,
    maxReminderMinutes: 120,
};

function readRuntimeInfo() {
    try {
        const raw = localStorage.getItem(APP_RUNTIME_INFO_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

function compressImage(file, maxSize = 1024) {
    return new Promise((resolve) => {
        try {
            const img = new Image();
            const objectUrl = URL.createObjectURL(file);
            img.onerror = () => {
                URL.revokeObjectURL(objectUrl);
                resolve(file);
            };
            img.onload = () => {
                URL.revokeObjectURL(objectUrl);
                try {
                    const canvas = document.createElement('canvas');
                    let { width, height } = img;
                    if (width > maxSize || height > maxSize) {
                        if (width > height) {
                            height = Math.round((height * maxSize) / width);
                            width = maxSize;
                        } else {
                            width = Math.round((width * maxSize) / height);
                            height = maxSize;
                        }
                    }
                    canvas.width = width;
                    canvas.height = height;
                    canvas.getContext('2d').drawImage(img, 0, 0, width, height);
                    canvas.toBlob((blob) => {
                        if (!blob) {
                            resolve(file);
                            return;
                        }
                        resolve(new File([blob], file.name, { type: 'image/jpeg' }));
                    }, 'image/jpeg', 0.8);
                } catch {
                    resolve(file);
                }
            };
            img.src = objectUrl;
        } catch {
            resolve(file);
        }
    });
}

async function dataUrlToFile(dataUrl, filename = 'medication-photo.jpg') {
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    return new File([blob], filename, { type: blob.type || 'image/jpeg' });
}

function mapMedicationToForm(med) {
    return {
        name: med.name || '',
        memo: med.memo || '',
        category: med.category || 'OTHER',
        intakeForm: med.intakeForm || 'ORAL',
        itemSeq: med.itemSeq || null,
        medicineSource: med.medicineSource || (med.itemSeq ? 'DUR' : null),
        imageUrl: med.imageUrl || '',
        frequencyType: med.frequencyType || 'DAILY',
        specificDays: med.specificDays ? med.specificDays.split(',').filter(Boolean) : [],
        hourlyInterval: med.hourlyInterval || '',
        intervalDays: med.intervalDays || '',
        cycleDaysOn: med.cycleDaysOn || '',
        cycleDaysOff: med.cycleDaysOff || '',
        monthlyDays: med.monthlyDays ? med.monthlyDays.split(',').map(Number).filter(Boolean) : [],
        dosageMethod: med.dosageMethod || 'ANYTIME',
        startDate: med.startDate || '',
        endDate: med.endDate || '',
        indefinite: !med.endDate,
        times: (med.times || []).map(time => String(time).slice(0, 5)).filter(Boolean),
        dosagePerTime: med.dosagePerTime || 1,
        alertType: med.alertType || 'PUSH',
        alarmVolume: med.alarmVolume ?? 80,
        reminderEnabled: (med.maxReminderMinutes || 0) > 0,
        reminderIntervalMinutes: med.initialIntervalMinutes || 30,
        maxReminderMinutes: med.maxReminderMinutes || 120,
    };
}

function buildPayloadFrom(form) {
    const asNeeded = form.frequencyType === 'AS_NEEDED';
    const interval = Number(form.reminderIntervalMinutes) || 0;
    const maxReminder = Number(form.maxReminderMinutes) || 0;
    return {
        name: form.name.trim(),
        memo: (form.memo || '').trim(),
        category: form.category || 'OTHER',
        intakeForm: form.intakeForm || 'ORAL',
        itemSeq: form.itemSeq || null,
        medicineSource: form.medicineSource || null,
        frequencyType: form.frequencyType || 'DAILY',
        specificDays: form.frequencyType === 'SPECIFIC_DAYS'
            ? (form.specificDays || []).slice().sort((a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b)).join(',')
            : null,
        hourlyInterval: form.frequencyType === 'HOURLY_INTERVAL' ? Number(form.hourlyInterval) || null : null,
        intervalDays: form.frequencyType === 'EVERY_N_DAYS' ? Number(form.intervalDays) || null : null,
        cycleDaysOn: form.frequencyType === 'CYCLE' ? Number(form.cycleDaysOn) || null : null,
        cycleDaysOff: form.frequencyType === 'CYCLE' ? Number(form.cycleDaysOff) || null : null,
        monthlyDays: form.frequencyType === 'MONTHLY' ? (form.monthlyDays || []).join(',') : null,
        dosageMethod: form.dosageMethod || 'ANYTIME',
        startDate: form.startDate,
        endDate: form.indefinite ? null : (form.endDate || null),
        indefinite: form.indefinite === true,
        times: asNeeded ? [] : (form.times || []).filter(Boolean),
        dosagePerTime: Number(form.dosagePerTime) || 1,
        alertType: form.alertType || 'PUSH',
        alarmVolume: form.alertType === 'FULLSCREEN' ? form.alarmVolume : null,
        initialIntervalMinutes: asNeeded ? 0 : (form.reminderEnabled ? interval : 0),
        escalationAfterMinutes: asNeeded ? 0 : (form.reminderEnabled ? maxReminder : 0),
        escalatedIntervalMinutes: asNeeded ? 0 : (form.reminderEnabled ? interval : 0),
        maxReminderMinutes: asNeeded ? 0 : (form.reminderEnabled ? maxReminder : 0),
    };
}

function editSaveError(error) {
    return error?.response?.status === 409
        ? localize('약 정보가 다른 곳에서 변경됐어요. 최신 정보를 확인하고 다시 수정해주세요')
        : localize('저장에 실패했어요');
}

export default function MedicationFormPage() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(initialForm);
    const [imagePreview, setImagePreview] = useState(null);
    const [existingMedications, setExistingMedications] = useState([]);
    const [runtimeInfo, setRuntimeInfo] = useState(() => readRuntimeInfo());
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
    const fileInputRef = useRef(null);
    const patchTimerRef = useRef(null);
    const pendingPatchFormRef = useRef(null);
    const editRevisionRef = useRef(null);
    const closingRef = useRef(false);
    const patchInFlightRef = useRef(null);
    const draftKey = `medication-draft:${id}`;
    const persistDraft = useCallback((snapshot) => {
        try {
            if (snapshot) sessionStorage.setItem(`medication-draft:${id}`, JSON.stringify({ form: snapshot, expectedEditRevision: editRevisionRef.current }));
            else sessionStorage.removeItem(`medication-draft:${id}`);
        } catch {}
    }, [id]);
    useEffect(() => {
        const beforeUnload = event => {
            if (!pendingPatchFormRef.current && !patchInFlightRef.current) return;
            event.preventDefault(); event.returnValue = '';
        };
        window.addEventListener('beforeunload', beforeUnload);
        return () => window.removeEventListener('beforeunload', beforeUnload);
    }, []);

    const flushPendingPatch = useCallback(() => {
        if (patchTimerRef.current) clearTimeout(patchTimerRef.current);
        patchTimerRef.current = null;
        if (!id) return Promise.resolve();
        if (patchInFlightRef.current) return patchInFlightRef.current;
        const drain = async () => {
            while (pendingPatchFormRef.current) {
                const snapshot = pendingPatchFormRef.current;
                pendingPatchFormRef.current = null;
                try {
                    const saved = await updateMedication(getDeviceUuid(), id, {
                        ...buildPayloadFrom(snapshot), expectedEditRevision: editRevisionRef.current,
                    });
                    editRevisionRef.current = saved.editRevision || null;
                    clearMedicationCaches();
                    if (pendingPatchFormRef.current) persistDraft(pendingPatchFormRef.current);
                    if (!pendingPatchFormRef.current) persistDraft(null);
                } catch (error) {
                    if (error?.response?.status === 409) {
                        let latest;
                        try {
                            latest = await getMedication(getDeviceUuid(), id);
                        } catch {
                            // Retain the pending edit if the conflict refresh also loses its response.
                            if (!pendingPatchFormRef.current) pendingPatchFormRef.current = snapshot;
                            throw error;
                        }
                        const conflicted = pendingPatchFormRef.current || snapshot;
                        if (JSON.stringify(buildPayloadFrom(conflicted)) === JSON.stringify(buildPayloadFrom(mapMedicationToForm(latest)))) {
                            // A previous request committed but its response was lost. No edit is missing.
                            pendingPatchFormRef.current = null;
                            editRevisionRef.current = latest.editRevision || null;
                            persistDraft(null);
                            setForm(mapMedicationToForm(latest));
                            setImagePreview(latest.imageUrl || null);
                            clearMedicationCaches();
                            continue;
                        }
                        // Preserve the user's text without replaying a stale full snapshot on reopening.
                        try { sessionStorage.setItem(`${draftKey}:conflict`, JSON.stringify({
                            form: conflicted, expectedEditRevision: editRevisionRef.current,
                        })); } catch {}
                        pendingPatchFormRef.current = null;
                        persistDraft(null);
                        editRevisionRef.current = latest.editRevision || null;
                        setForm(mapMedicationToForm(latest));
                        setImagePreview(latest.imageUrl || null);
                    } else if (!pendingPatchFormRef.current) pendingPatchFormRef.current = snapshot;
                    throw error;
                }
            }
            void syncWidget(getDeviceUuid());
        };
        patchInFlightRef.current = drain().finally(() => { patchInFlightRef.current = null; });
        return patchInFlightRef.current;
    }, [id, persistDraft, draftKey]);

    const photoEditor = useMedicationPhotoEditing({
        id, imageUrl: form.imageUrl, compress: compressImage,
        onSaved: url => {
            setForm(prev => ({ ...prev, imageUrl: url }));
            setImagePreview(url || null);
        },
        onError: (error, remove) => {
            const status = error?.response?.status;
            const message = status === 409
                ? translationKey('사진이 다른 곳에서 변경됐어요. 최신 사진을 확인하고 다시 시도해주세요')
                : status === 429 ? translationKey('오늘 사진 업로드 횟수를 모두 사용했어요 (10회/일)')
                : (status === 400 || status === 503) && error?.response?.data?.message ? error.response.data.message
                : remove ? translationKey('사진 삭제를 확인하지 못했어요. 사진을 확인하고 다시 시도해주세요')
                : translationKey('사진 저장을 확인하지 못했어요. 사진을 확인하고 다시 시도해주세요');
            setSnackbar({ open: true, message, severity: status === 429 ? 'info' : 'error' });
        },
    });
    const imageSaving = photoEditor.saving;

    const closeForm = useCallback(async () => {
        if (closingRef.current || photoEditor.busy.current) return;
        closingRef.current = true;
        setSaving(true);
        try {
            await flushPendingPatch();
            navigate('/medications', { replace: true });
        } catch (error) {
            setSnackbar({ open: true, message: editSaveError(error), severity: 'error' });
            closingRef.current = false;
        } finally {
            setSaving(false);
        }
    }, [flushPendingPatch, navigate, photoEditor.busy]);

    useBackHandler(true, closeForm, 0);

    useEffect(() => {
        if (!id) {
            navigate('/medications/new-bundle?mode=manual', { replace: true });
            return undefined;
        }

        let cancelled = false;
        (async () => {
            try {
                const uuid = getDeviceUuid();
                const [med, meds] = await Promise.all([
                    getMedication(uuid, id),
                    getMedications(uuid).catch(() => []),
                ]);
                if (cancelled) return;
                let savedDraft = null;
                try { savedDraft = JSON.parse(sessionStorage.getItem(draftKey)); } catch {}
                editRevisionRef.current = med.editRevision || null;
                const restorable = savedDraft?.expectedEditRevision && savedDraft.expectedEditRevision === med.editRevision;
                const alreadySaved = savedDraft?.form && JSON.stringify(buildPayloadFrom(savedDraft.form)) === JSON.stringify(buildPayloadFrom(mapMedicationToForm(med)));
                const nextForm = { ...(restorable ? savedDraft.form : mapMedicationToForm(med)), imageUrl: med.imageUrl || '' };
                if (savedDraft && !restorable) {
                    if (!alreadySaved) {
                        try { sessionStorage.setItem(`${draftKey}:conflict`, JSON.stringify(savedDraft)); } catch {}
                        setSnackbar({ open: true, message: editSaveError({ response: { status: 409 } }), severity: 'error' });
                    }
                    persistDraft(null);
                }
                if (restorable) {
                    pendingPatchFormRef.current = savedDraft.form;
                    void flushPendingPatch().catch(() => {
                        if (!cancelled) setSnackbar({ open: true, message: localize("저장하지 못한 편집을 복원했어요. 다시 저장해주세요"), severity: 'error' });
                    });
                }
                setForm(nextForm);
                setImagePreview(med.imageUrl || null);
                setExistingMedications((meds || []).filter(item => String(item.id) !== String(id)));
            } catch {
                if (!cancelled) {
                    setSnackbar({ open: true, message: localize("약 정보를 불러올 수 없어요"), severity: 'error' });
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
            void flushPendingPatch().catch(() => {});
            if (patchTimerRef.current) {
                clearTimeout(patchTimerRef.current);
                patchTimerRef.current = null;
            }
        };
    }, [id, navigate, flushPendingPatch, draftKey, persistDraft]);

    const onPatchField = useCallback((patch) => {
        setForm(prev => {
            const next = { ...prev, ...patch };
            if (!id) return next;

            pendingPatchFormRef.current = next;
            persistDraft(next);
            if (patchTimerRef.current) clearTimeout(patchTimerRef.current);
            patchTimerRef.current = setTimeout(async () => {
                try {
                    await flushPendingPatch();
                    setSnackbar({ open: true, message: localize("저장됐어요"), severity: 'success' });
                } catch (error) {
                    setSnackbar({ open: true, message: editSaveError(error), severity: 'error' });
                }
            }, 500);
            return next;
        });
    }, [id, flushPendingPatch, persistDraft]);

    const applySelectedImage = useCallback(async (file) => {
        if (!file || !id) return;
        if (file.size > 20 * 1024 * 1024) {
            setSnackbar({ open: true, message: localize("20MB 이하 이미지만 가능합니다"), severity: 'error' });
            return;
        }

        await photoEditor.save(file);
    }, [id, photoEditor.save]);

    const handleImageSelect = useCallback(async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        await applySelectedImage(file);
        if (fileInputRef.current) fileInputRef.current.value = '';
    }, [applySelectedImage]);

    const nativeImages = useNativeImageRequests(`medication:${id}`);

    const handleNativeMedicationImage = useCallback(async (message) => {
        if (message.type !== 'MEDICATION_IMAGE_RESULT') return;
        const request = nativeImages.take(message);
        if (!request) return;
        if (message.status === 'cancelled') return;
        if (message.status !== 'success') {
            setSnackbar({
                open: true,
                severity: message.status === 'permission_denied' ? 'warning' : 'error',
                message: message.message || localize("사진을 가져오지 못했어요"),
            });
            return;
        }

        try {
            const dataUrl = message.image?.dataUrl;
            if (!dataUrl) throw new Error('missing image');
            const file = await dataUrlToFile(dataUrl);
            if (request.isCurrent()) await applySelectedImage(file);
        } catch {
            setSnackbar({ open: true, message: localize("이미지를 불러올 수 없어요"), severity: 'error' });
        }
    }, [applySelectedImage, nativeImages]);

    useEffect(() => WebviewMessenger.onMessage(handleNativeMedicationImage), [handleNativeMedicationImage]);

    useEffect(() => {
        const handler = (event) => setRuntimeInfo(event.detail || readRuntimeInfo());
        window.addEventListener('app-runtime-info-updated', handler);
        return () => window.removeEventListener('app-runtime-info-updated', handler);
    }, []);

    const requestNativeMedicationImage = useCallback((source) => {
        if (source !== 'camera' && source !== 'library') return;
        if (WebviewMessenger.isInWebView()) {
            WebviewMessenger.send({ type: source === 'camera' ? 'TAKE_MEDICATION_PHOTO' : 'PICK_MEDICATION_IMAGE',
                requestId: nativeImages.begin('MEDICATION_IMAGE_RESULT', id) });
        }
    }, [nativeImages, id]);

    const clearImage = photoEditor.remove;

    if (loading) {
        return (
            <PageLoading backgroundColor="#fff" />
        );
    }

    const isNativeApp = WebviewMessenger.isInWebView();
    const nativeVersion = runtimeInfo?.appVersion || runtimeInfo?.runtimeVersion || '';
    const nativeMedicationPhotoBridgeSupported = supportsCorrelatedImagePicker(runtimeInfo) && isNativeApp
        && nativeVersion
        && compareVersions(nativeVersion, MIN_NATIVE_PHOTO_VERSION) >= 0;
    const directPhotoInput = isNativeApp && !nativeMedicationPhotoBridgeSupported;

    return (
        <>
            <WizardLayout
                step={0}
                totalSteps={1}
                headline={localize("약 정보를 수정하세요")}
                subtitle={localize("변경하면 자동으로 저장돼요")}
                ctaLabel={saving || imageSaving ? <LoadingLabel>{localize("저장 중...")}</LoadingLabel> : localize("완료")}
                ctaDisabled={saving || imageSaving}
                onBack={closeForm}
                onNext={closeForm}
                direction="forward"
            >
                <StepConfirm
                    form={form}
                    setForm={setForm}
                    isEdit
                    imagePreview={imagePreview}
                    onPatchField={onPatchField}
                    smartSource={null}
                    existingMedications={existingMedications.filter(med => med.frequencyType !== 'AS_NEEDED')}
                    fileInputRef={fileInputRef}
                    handleImageSelect={handleImageSelect}
                    imageSaving={imageSaving}
                    directPhotoInput={directPhotoInput}
                    onPickCamera={nativeMedicationPhotoBridgeSupported ? () => requestNativeMedicationImage('camera') : undefined}
                    onPickLibrary={nativeMedicationPhotoBridgeSupported ? () => requestNativeMedicationImage('library') : undefined}
                    clearImage={clearImage}
                />
            </WizardLayout>

            <AppToast
                open={snackbar.open}
                autoHideDuration={snackbar.severity === 'error' ? 4000 : 3000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                severity={snackbar.severity}
                message={localize(snackbar.message)}
            />
            <ScrollDownIndicator />
        </>
    );
}
